import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from '@mui/material';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { CommentTableProps, CommentColumn } from './types';
import {
  StyledTableContainer,
  StyledTable,
  StyledHeaderCell,
  StyledBodyCell,
  StyledTableRow,
  PaginationButton,
  TableWrapper,
  ShowAllButton,
} from './CommentTable.styled';

const CommentTable = <T extends object>({
  data = [],
  columns = [],
  pageSize: controlledPageSize = 5,
  showPagination = true,
  maxWidth = '100%',
  moduleName = 'Comments',
  // Server-side pagination props
  totalCount = 0,
  currentPage = 0,
  onPageChange,
  onPageSizeChange,
  isLoading = false,
  ...props
}: CommentTableProps<T>): React.ReactElement => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [pageSize, setPageSize] = useState(controlledPageSize);
  const [pageSizeAnchorEl, setPageSizeAnchorEl] = useState<null | HTMLElement>(null);

  const pageSizeOptions = [5, 10, 25, 50];

  // Calculate pagination for server-side pagination
  const totalPages = Math.ceil(totalCount / pageSize);
  const displayedData = data; // For server-side pagination, we display all data from server

  // Update local page size when controlled prop changes
  useEffect(() => {
    setPageSize(controlledPageSize);
  }, [controlledPageSize]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 0 && newPage < totalPages && onPageChange) {
      onPageChange(newPage);
    }
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    if (onPageSizeChange) {
      onPageSizeChange(newSize);
    }
    setPageSizeAnchorEl(null);
  };

  const handleShowAll = () => {
    const newSize = totalCount;
    setPageSize(newSize);
    if (onPageSizeChange) {
      onPageSizeChange(newSize);
    }
  };

  return (
    <TableWrapper maxWidth={maxWidth} data-testid="comment-table-wrapper">
      <Box>
        <Typography
          sx={{
            fontSize: '1.375rem',
            textTransform: 'uppercase',
            fontWeight: 700,
            lineHeight: '1.56',
            color: 'rgb(7, 49, 66)',
            fontFamily: "'Lato', sans-serif",
            [theme.breakpoints.down('sm')]: {
              fontSize: '1.25rem',
            },
          }}
          data-testid="comment-table-title"
        >
          {moduleName}
        </Typography>
      </Box>

      <StyledTableContainer
        sx={{
          maxHeight: '400px',
          overflowY: 'auto',
          overflowX: 'auto',
          position: 'relative',
        }}
        data-testid="comment-table-container"
      >
        {isLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1,
            }}
          >
            <CircularProgress size={40} />
          </Box>
        )}
        <StyledTable data-testid="comment-table">
          <TableHead data-testid="comment-table-head">
            <StyledTableRow data-testid="comment-table-header-row">
              {columns.map((column: CommentColumn) => (
                <StyledHeaderCell
                  key={column.accessor}
                  width={typeof column.width === 'number' ? `${column.width}px` : column.width || 'auto'}
                  data-testid={`comment-header-cell-${column.accessor}`}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: isMobile ? '10px' : '12px',
                        fontWeight: 700,
                        width: '100%',
                        textAlign: 'center',
                        color: 'rgba(0, 0, 0, 0.8)',
                        fontFamily: "'Lato', sans-serif",
                      }}
                      data-testid={`comment-header-text-${column.accessor}`}
                    >
                      {column.Header}
                    </Typography>
                  </Box>
                </StyledHeaderCell>
              ))}
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {displayedData.length === 0 ? (
              <StyledTableRow data-testid="comment-table-no-data">
                <StyledBodyCell colSpan={columns.length}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      py: 4,
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontStyle: 'italic' }}
                    >
                      {isLoading ? 'Loading comments...' : t('table.noComments', 'No comments available')}
                    </Typography>
                  </Box>
                </StyledBodyCell>
              </StyledTableRow>
            ) : (
              displayedData.map((row: any, index: number) => (
                <StyledTableRow
                  key={index}
                  className={index % 2 === 1 ? 'alternate' : ''}
                  data-testid={`comment-table-row-${index}`}
                  sx={{
                    '&.alternate': {
                      backgroundColor: '#f5f5f5',
                    },
                  }}
                >
                  {columns.map((column: CommentColumn) => (
                    <StyledBodyCell
                      key={column.accessor}
                      width={typeof column.width === 'number' ? `${column.width}px` : column.width || 'auto'}
                      data-testid={`comment-row-cell-${index}-${column.accessor}`}
                    >
                      <Typography
                        component="div"
                        sx={{
                          fontSize: isMobile ? '11px' : '12px',
                          textAlign: 'center',
                          wordBreak: column.accessor === 'comment' ? 'break-word' : 'normal',
                          maxWidth: column.accessor === 'comment' ? '300px' : 'auto',
                          fontFamily: "'Lato', sans-serif",
                        }}
                      >
                        {column.Cell 
                          ? column.Cell({ value: row[column.accessor] }) 
                          : row[column.accessor]
                        }
                      </Typography>
                    </StyledBodyCell>
                  ))}
                </StyledTableRow>
              ))
            )}
          </TableBody>
        </StyledTable>
      </StyledTableContainer>

      {/* Pagination controls - only show if pagination is enabled and we have server-side pagination handlers */}
      {showPagination && totalCount > 0 && onPageChange && onPageSizeChange && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 2 : 0,
            mt: 2,
          }}
          data-testid="comment-pagination-controls"
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: isMobile ? '12px' : '16px',
              width: isMobile ? '100%' : 'auto',
            }}
          >
            <ShowAllButton onClick={handleShowAll} data-testid="comment-show-all-button">
              {t(
                'table.showAllComments',
                isMobile ? `All ${totalCount}` : `Show All ${totalCount}`
              )}
            </ShowAllButton>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: isMobile ? 'flex-start' : 'center',
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? '12px' : '16px',
              width: isMobile ? '100%' : 'auto',
              mt: isMobile ? 2 : 0,
            }}
          >
            {!isMobile && (
              <Typography
                sx={{
                  fontSize: '14px',
                  color: 'rgba(0, 0, 0, 0.8)',
                  fontFamily: "'Lato', sans-serif",
                  fontWeight: 500,
                }}
              >
                {t('table.itemsPerPage', 'Items per page')}
              </Typography>
            )}

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                width: isMobile ? '100%' : 'auto',
                justifyContent: isMobile ? 'space-between' : 'flex-start',
              }}
            >
              {isMobile && (
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: 'rgba(0, 0, 0, 0.8)',
                    fontFamily: "'Lato', sans-serif",
                    fontWeight: 500,
                  }}
                >
                  {t('table.itemsPerPage', 'Items:')}
                </Typography>
              )}

              <PaginationButton
                endIcon={<KeyboardArrowDownIcon />}
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => setPageSizeAnchorEl(e.currentTarget)}
                size={isMobile ? 'small' : 'medium'}
                data-testid="comment-page-size-button"
              >
                {pageSize}
              </PaginationButton>

              <PaginationButton size={isMobile ? 'small' : 'medium'} data-testid="comment-page-navigation">
                <KeyboardArrowLeftIcon
                  onClick={() => handlePageChange(currentPage - 1)}
                  sx={{ 
                    opacity: currentPage === 0 ? 0.5 : 1, 
                    fontSize: isMobile ? 16 : 24,
                    cursor: currentPage === 0 ? 'not-allowed' : 'pointer',
                  }}
                  data-testid="comment-previous-page-button"
                />
                {currentPage + 1}
                <KeyboardArrowRightIcon
                  onClick={() => handlePageChange(currentPage + 1)}
                  sx={{
                    opacity: currentPage === totalPages - 1 ? 0.5 : 1,
                    fontSize: isMobile ? 16 : 24,
                    cursor: currentPage === totalPages - 1 ? 'not-allowed' : 'pointer',
                  }}
                  data-testid="comment-next-page-button"
                />
              </PaginationButton>
            </Box>
          </Box>
        </Box>
      )}

      {/* Page size menu */}
      <Menu
        anchorEl={pageSizeAnchorEl}
        open={Boolean(pageSizeAnchorEl)}
        onClose={() => setPageSizeAnchorEl(null)}
        PaperProps={{
          style: {
            maxHeight: 200,
            width: isMobile ? '100px' : 'auto',
          },
        }}
        data-testid="comment-page-size-menu"
      >
        {pageSizeOptions.map((size) => (
          <MenuItem
            key={size}
            onClick={() => handlePageSizeChange(size)}
            selected={size === pageSize}
            dense={isMobile}
          >
            {size}
          </MenuItem>
        ))}
      </Menu>
    </TableWrapper>
  );
};

export default CommentTable; 